import { ArrowRight } from 'lucide-react'
import { useAssessment } from '../../context/AssessmentContext'
import Shell from '../ui/Shell'
import { pulse } from '../ui/LightField'
import {
  AREA_BY_SLUG, COPY_GROUPS, FREQUENCY_OPTIONS, CONTROL_OPTIONS, CONTROL_QUESTION, QUIT_BUCKETS, HEADERS,
} from '../../data/onboarding'
import { sortByPriority } from '../../lib/priority'
import { getProgress, STEP_LABEL } from '../../lib/flow'

/**
 * "Where are you now?" — one screen per selected area. Status first, then the
 * follow-up the production app asks: frequency + control when currently using,
 * time since stopping when in recovery.
 */
function RecoveryGateStep() {
  const { state, dispatch } = useAssessment()
  const slugs = state.selectedAddictions
  const slug = slugs[state.currentAreaIndex]
  const area = AREA_BY_SLUG[slug]

  if (!area) {
    dispatch({ type: 'SET_SCREEN', payload: 'addictionSelection' })
    return null
  }

  const copy = COPY_GROUPS[area.copyGroup]
  const gate = state.gates[slug] || {}
  const complete =
    gate.status === 'active' ? gate.frequency != null && gate.control != null
      : gate.status === 'recovery' ? gate.daysClean != null
        : false

  const set = (patch, el) => {
    if (el) {
      const r = el.getBoundingClientRect()
      pulse(0.5, { x: (r.left + r.width / 2) / window.innerWidth, y: (r.top + r.height / 2) / window.innerHeight })
    }
    dispatch({ type: 'SET_GATE', payload: { slug, gate: patch } })
  }

  const setStatus = (status, el) => {
    // Switching status clears the other branch's answers.
    set({ status, frequency: undefined, control: undefined, daysClean: undefined }, el)
  }

  const next = () => {
    if (!complete) return
    if (state.currentAreaIndex < slugs.length - 1) {
      dispatch({ type: 'NEXT_AREA' })
      return
    }
    if (slugs.length > 1) {
      // Recommend the highest-priority area; the user can still override.
      const gatesWithThis = { ...state.gates, [slug]: { ...gate } }
      const top = sortByPriority(slugs, (s) => gatesWithThis[s])[0]
      dispatch({ type: 'SET_PRIMARY', payload: { slug: top, overridden: false } })
      dispatch({ type: 'SET_SCREEN', payload: 'prioritization' })
      return
    }
    dispatch({ type: 'SET_PRIMARY', payload: { slug: slugs[0], overridden: false } })
    dispatch({ type: 'SET_SCREEN', payload: state.gates[slugs[0]]?.status === 'recovery' ? 'barc10' : 'screener' })
  }

  const back = () => {
    if (state.currentAreaIndex > 0) dispatch({ type: 'PREV_AREA' })
    else dispatch({ type: 'SET_SCREEN', payload: 'addictionSelection' })
  }

  return (
    <Shell label={STEP_LABEL.recoveryGate} progress={getProgress(state)} onBack={back}>
      <main className="flow">
        <div className="q" key={slug}>
          <div className="q__meta">
            <span>{state.currentAreaIndex + 1} of {slugs.length}</span>
            <span>{area.label}</span>
          </div>

          <div className="q__body">
            <h1 className="q__text">{HEADERS.recoveryGate}</h1>
            <p className="q__hint" style={{ marginTop: 10 }}>For {area.label.toLowerCase()}.</p>
          </div>

          <div className="q__body rows" style={{ animationDelay: '40ms' }} role="radiogroup" aria-label="Current status">
            {[
              { key: 'active', label: copy.activeLabel },
              { key: 'recovery', label: copy.recoveryLabel },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="radio"
                aria-checked={gate.status === opt.key}
                className="row row--no-key"
                onClick={(e) => setStatus(opt.key, e.currentTarget)}
              >
                <span className="row__title">{opt.label}</span>
                <span />
              </button>
            ))}
          </div>

          {gate.status === 'active' && (
            <>
              <div className="q__body sub-q">
                <p className="sub-q__label">HOW OFTEN?</p>
                <div className="rows rows--2col" role="radiogroup" aria-label="How often">
                  {FREQUENCY_OPTIONS.map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      role="radio"
                      aria-checked={gate.frequency === o.value}
                      className="row row--no-key"
                      onClick={(e) => set({ frequency: o.value }, e.currentTarget)}
                    >
                      <span className="row__title">{o.label}</span>
                      <span />
                    </button>
                  ))}
                </div>
              </div>
              <div className="q__body sub-q">
                <p className="sub-q__label">{CONTROL_QUESTION.toUpperCase()}</p>
                <div className="rows rows--2col" role="radiogroup" aria-label={CONTROL_QUESTION}>
                  {CONTROL_OPTIONS.map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      role="radio"
                      aria-checked={gate.control === o.value}
                      className="row row--no-key"
                      onClick={(e) => set({ control: o.value }, e.currentTarget)}
                    >
                      <span className="row__title">{o.label}</span>
                      <span />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {gate.status === 'recovery' && (
            <div className="q__body sub-q">
              <p className="sub-q__label">WHEN DID YOU STOP?</p>
              <div className="rows" role="radiogroup" aria-label="When did you stop">
                {QUIT_BUCKETS.map((b) => (
                  <button
                    key={b.label}
                    type="button"
                    role="radio"
                    aria-checked={gate.daysClean === b.days}
                    className="row row--no-key"
                    onClick={(e) => set({ daysClean: b.days }, e.currentTarget)}
                  >
                    <span className="row__title">{b.label}</span>
                    <span />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flow__actions">
          <button type="button" className="btn btn--block" onClick={next} disabled={!complete}>
            Continue
            <ArrowRight size={18} strokeWidth={2.25} />
          </button>
        </div>
      </main>
    </Shell>
  )
}

export default RecoveryGateStep
